/**
 * Credit memo (credit work order) operations.
 *
 * Extracted verbatim from workOrdersService.ts — functions are invoked with
 * `this` bound to the WorkOrdersService instance (WorkOrdersCtx).
 */
import { extractItems } from '../../utils/apiHelpers';
/**
 * Get the credit context for a CLOSED work order: its creditable service
 * lines with already-credited and remaining amounts (decimal strings,
 * positive magnitudes), plus credits already issued against it.
 * Requires workorders:credit.
 */
export async function getCreditContext(workOrderId) {
    try {
        const response = await this.client.get(`/work-orders/${workOrderId}/credit-context`);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to fetch credit context');
        }
        const data = response.data;
        return {
            original_work_order: this.normalizeWorkOrder(data.original_work_order || {}),
            lines: Array.isArray(data.lines) ? data.lines : [],
            credit_work_orders: Array.isArray(data.credit_work_orders)
                ? data.credit_work_orders.map((wo) => this.normalizeWorkOrder(wo))
                : [],
        };
    }
    catch (error) {
        console.error('[WorkOrdersService] Get credit context error:', error);
        throw error;
    }
}
/**
 * Issue a standalone credit memo (credit work order) against a CLOSED work
 * order. Amounts in the payload are POSITIVE magnitudes; omitted field =
 * credit full remaining, explicit 0 = skip field. Preserves the structured
 * errorBody (like createWorkOrder) so callers can parse the backend
 * {error, code} envelope (CREDIT_OVER_CAP, CREDIT_OF_CREDIT, …) via
 * parseCreditError.
 */
export async function createCredit(workOrderId, payload) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/credits`, payload);
        if (!response.success || !response.data) {
            const err = new Error(response.error || 'Failed to create credit memo');
            if (response.errorBody)
                err.errorBody = response.errorBody;
            throw err;
        }
        const wo = response.data.work_order || response.data;
        return this.normalizeWorkOrder(wo);
    }
    catch (error) {
        console.error('[WorkOrdersService] Create credit memo error:', error);
        throw error;
    }
}
/**
 * List credit work orders issued against this (original) work order.
 */
export async function listCredits(workOrderId) {
    try {
        const response = await this.client.get(`/work-orders/${workOrderId}/credits`);
        if (!response.success) {
            throw new Error(response.error || 'Failed to list credits');
        }
        const items = extractItems(response.data, ['work_orders', 'credits']);
        return (items || []).map((wo) => {
            const summary = this.normalizeWorkOrder(wo);
            // Pass the total through when the backend includes one (decimal
            // string, negative for credits) — the credits chip displays it.
            if (wo.total_amount !== undefined && wo.total_amount !== null)
                summary.total_amount = wo.total_amount;
            return summary;
        });
    }
    catch (error) {
        console.error('[WorkOrdersService] List credits error:', error);
        throw error;
    }
}
